import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { agenciesApi, aiApi, workspacesApi, type Workspace } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { Loader2, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';

export function SettingsAI() {
  const { getToken } = useAuth();
  const { currentAgency, setCurrentAgency } = useAppStore();
  const qc = useQueryClient();

  const [aiModel, setAiModel] = useState(currentAgency?.ai_model ?? '@cf/meta/llama-3.1-8b-instruct');
  const [xaiKey, setXaiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    if (currentAgency) {
      setAiModel(currentAgency.ai_model ?? '@cf/meta/llama-3.1-8b-instruct');
    }
  }, [currentAgency?.id]);

  const { data: modelsData } = useQuery({
    queryKey: ['ai-models'],
    queryFn: async () => {
      const token = await getToken();
      return aiApi.models(token!);
    },
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const workspaces = workspacesData?.workspaces ?? [];

  const update = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return agenciesApi.update(token!, currentAgency!.id, {
        ai_model: aiModel,
        ...(xaiKey ? { xai_api_key: xaiKey } : {}),
      });
    },
    onSuccess: (data) => {
      setCurrentAgency(data.agency);
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setXaiKey('');
    },
  });

  if (!currentAgency) {
    return <p className="text-sm text-muted-foreground">No agency selected</p>;
  }

  const textModels = modelsData?.text;

  return (
    <div className="max-w-lg space-y-8">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1">Agency AI Settings</h2>
        <p className="text-xs text-muted-foreground">
          Set the default AI model for all workspaces in this agency. Individual workspaces can override this below.
        </p>
      </div>

      {/* Agency-level defaults */}
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-foreground block mb-1.5">
            Default AI Model
          </label>
          <select
            value={aiModel}
            onChange={(e) => setAiModel(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
          >
            <optgroup label="Cloudflare Workers AI">
              {(textModels?.cloudflare ?? []).map((m: { id: string; name: string }) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
            <optgroup label="xAI (requires API key)">
              {(textModels?.xai ?? []).map((m: { id: string; name: string }) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div className="bg-muted/30 rounded-lg p-3 border border-border">
          <p className="text-xs font-medium text-foreground mb-1">Image Generation</p>
          <p className="text-xs text-muted-foreground">
            Available: FLUX.1 Schnell (fast), Stable Diffusion XL, Grok-2-Image (with xAI key)
          </p>
        </div>

        <div>
          <label className="text-xs font-medium text-foreground block mb-1.5">
            xAI API Key
            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Required for Grok text and Grok-2 image generation
          </p>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={xaiKey}
              onChange={(e) => setXaiKey(e.target.value)}
              placeholder={currentAgency.xai_api_key ? '••••••••••••••••' : 'xai-...'}
              className="w-full bg-input border border-border rounded-md px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {currentAgency.xai_api_key && !xaiKey && (
            <p className="text-xs text-success mt-1">✓ xAI API key is configured</p>
          )}
        </div>

        <button
          onClick={() => update.mutate()}
          disabled={update.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm hover:bg-accent/90 disabled:opacity-50"
        >
          {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save Agency AI Settings
        </button>
      </div>

      {/* Per-workspace overrides */}
      {workspaces.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-1">Workspace Overrides</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Override the agency default for individual workspaces.
          </p>
          <div className="space-y-3">
            {workspaces.map((ws) => (
              <WorkspaceAIOverride
                key={ws.id}
                workspace={ws}
                agencyModel={aiModel}
                modelsData={modelsData}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WorkspaceAIOverride({
  workspace,
  agencyModel,
  modelsData,
}: {
  workspace: Workspace;
  agencyModel: string;
  modelsData: { text?: { cloudflare: { id: string; name: string }[]; xai: { id: string; name: string }[] } } | undefined;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [model, setModel] = useState(workspace.ai_model_override ?? '');
  const [key, setKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return workspacesApi.update(token!, workspace.id, {
        ai_model_override: model || null,
        xai_api_key_override: key || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      setKey('');
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return workspacesApi.update(token!, workspace.id, {
        ai_model_override: null,
        xai_api_key_override: null,
      });
    },
    onSuccess: () => {
      setModel('');
      setKey('');
      qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const hasOverride = !!workspace.ai_model_override || !!workspace.xai_api_key_override;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-sm font-medium text-foreground">{workspace.name}</span>
          {hasOverride && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/20 text-accent font-medium">
              Override active
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {workspace.ai_model_override ?? `Using agency default`}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border bg-muted/10">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">AI Model Override</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">Use agency default ({agencyModel})</option>
              <optgroup label="Cloudflare Workers AI">
                {(modelsData?.text?.cloudflare ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </optgroup>
              <optgroup label="xAI (requires API key)">
                {(modelsData?.text?.xai ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">
              xAI API Key Override
              <span className="text-muted-foreground font-normal ml-1">(optional)</span>
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder={workspace.xai_api_key_override ? '••••••••••••••••' : 'xai-... (leave blank to use agency key)'}
                className="w-full bg-input border border-border rounded-md px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {workspace.xai_api_key_override && !key && (
              <p className="text-xs text-success mt-1">✓ Workspace xAI key is configured</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="flex items-center gap-2 px-3 py-1.5 bg-accent text-accent-foreground rounded-md text-xs hover:bg-accent/90 disabled:opacity-50"
            >
              {save.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              Save Override
            </button>
            {hasOverride && (
              <button
                onClick={() => clear.mutate()}
                disabled={clear.isPending}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10 disabled:opacity-50 transition-colors"
              >
                Clear Override
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
